<!--
  The button that saves a box's text (a new draft, a reply, an edit), and
  how that save stands (see `Writes.box`): its label while idle; from the
  press on, a spinner and what is being done, taking no second press; and
  after a failure, why beside it, with the button sending the box again.
  It keeps the focus while saving (`aria-disabled`, not `disabled`), so
  the keys do not fall through to the page.
-->
<script lang="ts">
  import type { Save } from '../state/pending.svelte';
  import ErrorNotice from './ErrorNotice.svelte';

  interface Props {
    save: Save;
    /** What it does while idle ("Add draft"). */
    label: string;
    /** Nothing to save, or saving is off here (`title` says why). */
    disabled?: boolean;
    title?: string;
    onsave: () => void;
  }
  let { save, label, disabled = false, title, onsave }: Props = $props();

  const saving = $derived(save.at === 'saving');
</script>

{#if save.at === 'failed'}
  <span class="save-error" role="alert">{save.label} failed: <ErrorNotice text={save.error.text} link={save.error.link} linkText={save.error.linkText} /></span>
{/if}
<button
  class="micro save-button"
  data-save={save.at}
  aria-busy={saving ? 'true' : undefined}
  aria-disabled={saving ? 'true' : undefined}
  {disabled}
  {title}
  onclick={() => !saving && onsave()}
  >{#if save.at === 'saving'}<span class="spinner" aria-hidden="true"></span>{save.label}…{:else if save.at === 'failed'}Retry{:else}{label}{/if}</button
>

<style>
  .save-button {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: var(--fg);
    color: var(--bg);
  }
  .save-button[aria-disabled='true'] {
    opacity: 0.7;
    cursor: progress;
  }
  .save-button[aria-disabled='true']:hover {
    border-color: var(--line);
  }
  .save-error {
    flex: 0 1 auto;
    min-width: 0;
    font-size: 11px;
    line-height: 1.4;
    color: var(--bad);
  }
</style>
